import {useLazyQuery, useMutation, type FetchResult} from '@apollo/client';
import {useCallback} from 'react';

import {hasValue} from '@famly/stat_ts-utils_has-value';
import {CreatePlan, GetPlansPreview} from 'web-app/react/plans/manage-plan/queries';
import type {
    CreatePlanMutation,
    CreatePlanMutationVariables,
    PreviewPlansQuery,
    PreviewPlansQueryVariables,
} from 'web-app/react/plans/manage-plan/__generated__/queries.api-types';
import {isNonEmpty} from 'web-app/util/non-empty-array';
import {isFailed} from 'web-app/util/failable';
import * as Warnable from 'web-app/util/warnable';
import * as SentryService from 'web-app/services/sentry';

import type {SubmissionData} from './use-submission-data';
import {
    transformApiPreviewPlanToPlanValidationResult,
    transformApiSessionToTuition,
    transformSubmissionDataPlansToPlanInput,
} from '../transformers/api-transformers';
import type {PlanValidationError, Tuition, BillingProfile} from '../types';
import {useSubmitTuition} from './use-submit-tuition';
import {type TuitionsWithOverriddenPrice} from './use-submission-data';

type Result = {
    validateAndEditPlan: ({
        plans,
        tuitionsWithOverriddenPrice,
        selectedTuitions,
        siteId,
        automaticRuleGroupId,
        oldPlanId,
        isPlanPartiallyBilled,
        billingProfiles,
    }: {
        plans: SubmissionData['plans'];
        tuitionsWithOverriddenPrice: SubmissionData['tuitionsWithOverriddenPrice'];
        selectedTuitions: Tuition[];
        siteId: string;
        automaticRuleGroupId: string | null;
        oldPlanId: string;
        isPlanPartiallyBilled: boolean;
        billingProfiles: BillingProfile[];
    }) => ValidateAndEditPlanResult;
};

type ValidateAndEditPlanResult = Promise<
    | {type: 'edited-plan'; result: FetchResult<CreatePlanMutation>}
    | {
          type: 'validation-errors';
          errors: Array<
              | {
                    type: 'error';
                    errors: PlanValidationError[];
                }
              | {
                    type: 'server-error';
                    message: string;
                    error: number;
                }
          >;
      }
>;

export const useValidateAndEditPlan = (): Result => {
    const [validatePlan] = useLazyQuery<PreviewPlansQuery, PreviewPlansQueryVariables>(GetPlansPreview);

    const [editBillingPlan] = useMutation<CreatePlanMutation, CreatePlanMutationVariables>(CreatePlan);

    const {submit: createTuition} = useSubmitTuition();

    const createTuitionsWithOverriddenPrice = useCallback(
        async (
            tuitionsWithOverriddenPrice: TuitionsWithOverriddenPrice,
            billingProfiles: BillingProfile[],
        ): Promise<Tuition[]> => {
            if (!isNonEmpty(tuitionsWithOverriddenPrice.overriddenPriceTuitions)) {
                return [];
            }

            const createdTuitions = await Promise.all(
                tuitionsWithOverriddenPrice.overriddenPriceTuitions.map(transformedTuition =>
                    createTuition(transformedTuition.newTuition),
                ),
            );
            if (createdTuitions.some(createdTuition => !hasValue(createdTuition.data))) {
                return Promise.reject('Some tuitions with overridden prices were not created.');
            }

            const newTuitionsAndWarnings = createdTuitions.map(createdTuition =>
                transformApiSessionToTuition({
                    billingProfiles,
                    hiddenTuitionsToDisplay: [createdTuition.data!.createSessionPackage.session.id],
                })(createdTuition.data!.createSessionPackage.session),
            );

            const newTuitions = Warnable.dispatchWarnings(Warnable.all(newTuitionsAndWarnings), warnings => {
                SentryService.captureMessage(
                    `Tuitions with overridden prices warnings: ${warnings.map(w => w.warning).join(', ')}`,
                );
            });

            if (!newTuitions.every(hasValue)) {
                return Promise.reject('Some tuitions with overridden prices were not transformed.');
            }

            return newTuitions;
        },
        [createTuition],
    );

    const validateAndEditPlan = useCallback(
        async ({
            plans,
            tuitionsWithOverriddenPrice,
            selectedTuitions,
            siteId,
            automaticRuleGroupId,
            oldPlanId,
            isPlanPartiallyBilled,
            billingProfiles,
        }: {
            plans: SubmissionData['plans'];
            tuitionsWithOverriddenPrice: SubmissionData['tuitionsWithOverriddenPrice'];
            selectedTuitions: Tuition[];
            siteId: string;
            automaticRuleGroupId: string | null;
            oldPlanId: string;
            isPlanPartiallyBilled: boolean;
            billingProfiles: BillingProfile[];
        }): ValidateAndEditPlanResult => {
            const tuitionsWithoutOverrides = selectedTuitions.filter(tuition => !hasValue(tuition.overriddenPrice));

            const newTuitionsWithOverrides = await (async () => {
                // NOTE: Interpreting missing tuitionsWithOverriddenPrice as
                // the absence of tuitions with overrides (i.e. not an error).
                if (!hasValue(tuitionsWithOverriddenPrice)) {
                    return [];
                }
                return await createTuitionsWithOverriddenPrice(tuitionsWithOverriddenPrice, billingProfiles);
            })();

            const planInputs = plans({selectedTuitions: [...tuitionsWithoutOverrides, ...newTuitionsWithOverrides]});
            const planInputsFailable = transformSubmissionDataPlansToPlanInput(
                planInputs,
                automaticRuleGroupId,
                oldPlanId,
                isPlanPartiallyBilled,
            );

            if (isFailed(planInputsFailable)) {
                throw new Error(planInputsFailable.error.join(', '));
            }

            const validationInputs = {
                planInputs: planInputsFailable.value,
                siteId,
            };
            const validationResult = await validatePlan({
                variables: validationInputs,
            });

            if (!validationResult.data || !isNonEmpty(validationResult.data.finance.plans.preview)) {
                return {
                    type: 'validation-errors',
                    errors: [
                        {
                            type: 'server-error',
                            message: 'No data returned from the server',
                            error: 500,
                        },
                    ],
                };
            }

            const planValidationResults = validationResult.data.finance.plans.preview.map(p => {
                const plan = transformApiPreviewPlanToPlanValidationResult(p);
                return plan;
            });

            if (planValidationResults.some(result => result.type === 'error' || result.type === 'server-error')) {
                return {
                    type: 'validation-errors',
                    errors: planValidationResults.filter(
                        result => result.type === 'error' || result.type === 'server-error',
                    ),
                };
            }

            const editedPlan = await editBillingPlan({
                variables: validationInputs,
            });

            return {type: 'edited-plan', result: editedPlan};
        },
        [validatePlan, editBillingPlan, createTuitionsWithOverriddenPrice],
    );

    return {validateAndEditPlan};
};
